import { apiService } from './api.service.js'
import { authService } from './auth.service.js'
import { cacheService } from './cache.service.js'
import { saldoMensalCacheService } from './saldo-mensal-cache.service.js'
import { AUTH_CONSTANTS } from '../constants/auth.constants.js'

/**
 * Serviço de inicialização do sistema
 * Responsável por carregar caches essenciais no startup via endpoint
 * batch consolidado (GET /api/startup) e pré-carregar dados após
 * autenticação para melhorar a experiência do usuário.
 *
 * Estratégia:
 * 1. No initialize(), tenta carregar todos os dados mestres de uma vez
 *    via endpoint batch (contas, categorias, responsáveis, tags, saldoMensal).
 * 2. Se o batch falha, faz fallback para carregamento sob demanda (comportamento anterior).
 * 3. Após autenticação, pré-carrega saldo mensal em background (fire-and-forget).
 *
 * Isso reduz de 5+ requisições HTTP separadas para 1 requisição consolidada
 * no carregamento inicial, melhorando significativamente a latência percebida.
 */
export class StartupService {
  constructor () {
    this.initialized = false

    // Escuta evento de autenticação para pré-carregar dados
    window.addEventListener('auth-state-changed', this._handleAuthStateChanged.bind(this))
  }

  /**
   * Inicializa sistema aguardando autenticação antes de carregar caches.
   *
   * Fluxo:
   * 1. Aguarda authService.waitForAuth() com timeout de 10s (AUTH_CONSTANTS.AUTH_TIMEOUT)
   * 2. Se autenticado: chama GET /api/startup e popula caches via _popularCaches()
   * 3. Se não autenticado: encerra sem chamada API, marca initialized=true
   * 4. Se timeout: emite console.warn(), marca initialized=true, sem batch
   *
   * Isso garante que nenhuma requisição à API ocorra antes do estado de
   * autenticação do Firebase estar resolvido, evitando erros 401 no startup.
   *
   * @returns {Promise<void>}
   */
  async initialize () {
    if (this.initialized) return

    /** @type {number|null} ID do timer de timeout para limpeza */
    let timeoutId = null

    try {
      // Aguarda auth com timeout de segurança (10s)
      const authResolved = await Promise.race([
        authService.waitForAuth().then(() => true),
        new Promise(resolve => {
          timeoutId = setTimeout(() => resolve(false), AUTH_CONSTANTS.AUTH_TIMEOUT)
        })
      ])

      // Limpa timeout se auth resolveu primeiro
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }

      // Timeout: auth não resolveu dentro do prazo
      if (!authResolved) {
        console.warn('⚠️ Auth timing guard: timeout de autenticação no startup')
        this.initialized = true
        return
      }

      // Não autenticado: encerra sem chamada API
      if (!authService.isAuthenticated()) {
        this.initialized = true
        return
      }

      // Autenticado: carrega batch normalmente
      const response = await apiService.get('startup')

      if (response.success && response.data) {
        this._popularCaches(response.data)
        console.info('🚀 Sistema EquiLibra v2 inicializado (batch)')
      }
    } catch (error) {
      // Limpa timeout em caso de erro inesperado
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
      // Fallback: carregamento individual sob demanda (comportamento anterior)
      console.warn('⚠️ Startup batch falhou, usando carregamento sob demanda:', error.message)
    }

    this.initialized = true
  }

  /**
   * Verifica se sistema foi inicializado
   * @returns {boolean}
   */
  isInitialized () {
    return this.initialized
  }

  /**
   * Popula caches com dados recebidos do endpoint batch
   * Cada recurso é populado apenas se presente na resposta.
   * Usa cacheService.set() para manter o mesmo formato e TTL
   * que o carregamento individual usaria.
   *
   * @param {Object} dados - Dados retornados pelo endpoint /api/startup
   * @param {Array} [dados.contas] - Lista de contas ativas
   * @param {Array} [dados.categorias] - Lista de categorias ativas
   * @param {Array} [dados.responsaveis] - Lista de responsáveis ativos
   * @param {Array} [dados.tags] - Lista de tags ativas
   * @param {Object|null} [dados.saldoMensal] - Saldo mensal do mês atual
   * @private
   */
  _popularCaches (dados) {
    if (dados.contas) {
      cacheService.set('contas', dados.contas)
    }
    if (dados.categorias) {
      cacheService.set('categorias', dados.categorias)
    }
    if (dados.responsaveis) {
      cacheService.set('responsaveis', dados.responsaveis)
    }
    if (dados.tags) {
      cacheService.set('tags', dados.tags)
    }
    if (dados.saldoMensal) {
      const mes = String(new Date().getMonth() + 1).padStart(2, '0')
      const ano = String(new Date().getFullYear())
      cacheService.set(`saldo_mensal_${mes}_${ano}`, dados.saldoMensal)
    }
  }

  /**
   * Handler para mudança de estado de autenticação
   * Quando o usuário é autenticado, pré-carrega o saldo do mês atual
   * em background (fire-and-forget) para que o dashboard já tenha
   * dados em cache quando o usuário navegar.
   *
   * O pré-carregamento não bloqueia o fluxo de startup — erros são
   * silenciados pois o dashboard fará nova tentativa se necessário.
   *
   * @param {CustomEvent} event - Evento 'auth-state-changed' com detail { isAuthenticated }
   * @private
   */
  _handleAuthStateChanged (event) {
    const { isAuthenticated } = event?.detail || {}

    if (isAuthenticated) {
      this._preCarregarSaldoMensal()
    }
  }

  /**
   * Pré-carrega saldo do mês atual em background
   * Usa fire-and-forget para não bloquear o startup.
   * Se falhar, o dashboard fará nova tentativa ao ser renderizado.
   *
   * @private
   */
  _preCarregarSaldoMensal () {
    saldoMensalCacheService.obterMesAtual().catch(() => {
      // Silencia erros — o dashboard tentará novamente quando renderizado
    })
  }
}

// Instância singleton
export const startupService = new StartupService()
